import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "components/button";
import { OnCallBadge } from "components/OnCallBadge";
import { format } from "date-fns";
import React from "react";
import { RosterBreak } from "roster/RosterBreak";
import { useAppSelector } from "store/hooks";
import { selectRosteredShiftById, ShiftObj } from "store/reducers";
import { selectIsManager } from "store/reducers/config/configSlice";

import { TableCell } from "./table_cell";

type Props = {
  rosteredShiftId: number;
  revertShiftToRosteredHours: (rosteredShift: RosteredShift) => void;
  revertStartTime?: (rosteredShift: RosteredShift, shiftObj: ShiftObj) => void;
  revertEndTime?: (rosteredShift: RosteredShift, shiftObj: ShiftObj) => void;
  revertToShift?: ShiftObj;
  onClickOnCall: (rosteredShift: RosteredShift) => void;
  onClickCallBack: (rosteredShift: RosteredShift) => void;
  onCallEvent?: OnCallEvent;
  recordedEventIsBeingAdded: boolean;
};

export const RosteredShiftRow: React.FC<Props> = (props) => {
  const {
    rosteredShiftId,
    revertShiftToRosteredHours,
    revertStartTime,
    revertEndTime,
    revertToShift,
    onClickOnCall,
    onClickCallBack,
    onCallEvent,
    recordedEventIsBeingAdded,
  } = props;
  const rosteredShift = useAppSelector((state) => selectRosteredShiftById(state, rosteredShiftId));
  const isManager = useAppSelector(selectIsManager);
  if (rosteredShift === undefined) {
    return null;
  }

  return (
    <tr key={rosteredShiftId}>
      <TableCell shiftType="rostered_shift">
        <span className="cursor-pointer font-medium text-primary-dark" title="Rostered shift">
          R
        </span>
      </TableCell>
      <TableCell shiftType="rostered_shift">
        <div className="grid place-items-center">
          {format(new Date(rosteredShift.start), "h:mm a")}
          {!rosteredShift.on_call && revertToShift && revertStartTime && (
            <Button
              size="sm"
              onClick={() => revertStartTime(rosteredShift, revertToShift)}
              title="Revert the above shift to the rostered start time"
            >
              Revert
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell shiftType="rostered_shift">
        <div className="grid place-items-center">
          {format(new Date(rosteredShift.end), "h:mm a")}
          {!rosteredShift.on_call && revertToShift && revertEndTime && (
            <Button
              size="sm"
              onClick={() => revertEndTime(rosteredShift, revertToShift)}
              title="Revert the above shift to the rostered end time"
            >
              Revert
            </Button>
          )}
        </div>
      </TableCell>
      {/* break */}
      <TableCell shiftType="rostered_shift">
        <RosterBreak rostered_break={rosteredShift.rostered_breaks[0]} />
      </TableCell>
      <TableCell shiftType="rostered_shift" />
      <TableCell shiftType="rostered_shift">
        <div>{rosteredShift.department.name}</div>
        {rosteredShift.on_call && <OnCallBadge />}
      </TableCell>
      <TableCell shiftType="rostered_shift">
        <div>{rosteredShift.location.name}</div>
        <div>{rosteredShift.notes}</div>
      </TableCell>
      <TableCell>
        {rosteredShift.on_call && isManager && (
          <div className="flex flex-col gap-1" data-track-context="timesheet.on_call">
            <Button
              size="sm"
              data-track="open_record"
              disabled={onCallEvent !== undefined || recordedEventIsBeingAdded}
              onClick={() => onClickOnCall(rosteredShift)}
            >
              Record On-Call
            </Button>
            {onCallEvent && (
              <Button
                size="sm"
                data-track-context="timesheet.call_back"
                data-track="open_record"
                disabled={recordedEventIsBeingAdded}
                onClick={() => onClickCallBack(rosteredShift)}
              >
                Record Call-Back
              </Button>
            )}
          </div>
        )}
        {!rosteredShift.on_call && (
          <Button
            onClick={() => revertShiftToRosteredHours(rosteredShift)}
            title="Create a new shift with exactly the rostered hours"
            size="lg"
          >
            <FontAwesomeIcon icon="history" /> Revert
          </Button>
        )}
      </TableCell>
    </tr>
  );
};
